import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, InterventionModal, SnoozeModal, useCan } from "components";
import { usePermanentRealTimeUser, useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES, SNOOZE_STATUSES } from "models";
import {
  useAppendQuestionResultSnoozeMutation,
  useDeleteQuestionResultSnoozeMutation
} from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResultActionSelector/hooks";
import { useTyreTitleAndSeasons } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks/useTyreTitleAndSeasons";
import {
  useDeleteInterventionSnoozeMutation,
  useInterventionAppendSnoozeMutation
} from "modules/AppointmentDetails/components/Interventions/components/InterventionActionSelector/hooks";
import "modules/AppointmentDetails/components/SnoozedItems/components/SnoozeActionSelector/SnoozeActionSelector.scss";
import { useSnoozedItemsHistory } from "modules/AppointmentDetails/components/SnoozedItems/components/SnoozedItemsHistoryTable/hooks";
import { useAppointmentDealer, useAppointmentLocation, useCanConvertToIntervention } from "modules/AppointmentDetails/hooks";
import { convertQuestionResultToIntervention } from "modules/AppointmentDetails/utils";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";

import type { DeleteSnoozePayload } from "components";
import type { Appointment, Intervention, QuestionResult, Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { InterventionSnoozeData } from "modules/AppointmentDetails/components/Interventions/components";
import type { SnoozeAttachments } from "modules/AppointmentDetails/components/SnoozedItems/components/SnoozeActions";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type SnoozeActionSelectorProps = {
  appointment: Appointment;
  snoozeItem: Snooze;
  intervention?: Intervention;
  questionResult?: QuestionResult;
  attachments?: SnoozeAttachments[];
  snoozedItems?: (QuestionResult | Intervention)[];
};

export const SnoozeActionSelector = ({ snoozeItem, appointment, snoozedItems, intervention, questionResult, attachments = [] }: SnoozeActionSelectorProps) => {
  const { t } = useTranslation();

  const snoozeStatusCannotBeConverted =
    snoozeItem.snooze_status_id !== null && [SNOOZE_STATUSES.Done, SNOOZE_STATUSES.Approved, SNOOZE_STATUSES.Declined].includes(snoozeItem.snooze_status_id);
  const isAlreadyConverted =
    appointment.interventions?.some(i => i.question_result_id === questionResult?.id || i.id === intervention?.id || i.snooze_id === snoozeItem.id) ?? false;

  const { user } = usePermanentRealTimeUser();
  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const showPriceInVat = useShowPriceInVat();
  const { refetchSnoozedItemsHistory } = useSnoozedItemsHistory(appointment.id);
  const { tyreTitle, seasonOptions } = useTyreTitleAndSeasons(questionResult?.tyre_position);
  const canConvertToIntervention = useCanConvertToIntervention(appointmentLocation, isAlreadyConverted || snoozeStatusCannotBeConverted);
  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);

  const appendQuestionResultSnooze = useAppendQuestionResultSnoozeMutation();
  const deleteQuestionResultSnooze = useDeleteQuestionResultSnoozeMutation();
  const { mutateAsync: interventionAppendSnooze, isPending: isPendingInterventionAppendSnooze } = useInterventionAppendSnoozeMutation();
  const { mutateAsync: interventionDeleteSnooze, isPending: isPendingInterventionDeleteSnooze } = useDeleteInterventionSnoozeMutation();

  const [showConvertToInterventionModal, setShowConvertToInterventionModal] = useState<AddInterventionPayload | null>(null);
  const [showEditSnoozeQuestionResultModal, setShowEditSnoozeQuestionResultModal] = useState<QuestionResultData | null>(null);
  const [showEditSnoozedInterventionModal, setShowEditSnoozeInterventionModal] = useState<InterventionSnoozeData | null>(null);

  const canEditSnooze = canCreateSnooze && appointmentLocation?.schedule_enabled;

  const handleCloseSnoozeModals = () => {
    setShowEditSnoozeInterventionModal(null);
    setShowEditSnoozeQuestionResultModal(null);
  };

  const handleUpdateSnoozeItems = () => {
    refetchSnoozedItemsHistory(); // TODO: remove this call, instead do the mutation onSuccess properly
    handleCloseSnoozeModals();
  };

  const handleSnoozeStatusToDone = async (questionResultId?: number) => {
    const questionResult = snoozedItems?.find(i => i.id === questionResultId);
    if (!questionResult) return;

    const snoozeItem = questionResult.snooze_history?.reduce((acc, curr) => (acc.created_on > curr.created_on ? acc : curr));
    if (!snoozeItem || !canCreateSnooze) return;
    await interventionAppendSnooze({
      ...snoozeItem,
      question_result_id: questionResult.id,
      snooze_status_id: SNOOZE_STATUSES.Done,
      appointment_id: questionResult.appointment_id,
      note: `${t("v8_converted_snooze_to_intervention", "Converted snooze to intervention on")} WO #${appointment.wo_nr}`
    });
  };

  const handleEditSnoozeItem = () => {
    if (intervention)
      setShowEditSnoozeInterventionModal({
        note: intervention.internal_note,
        intervention_id: intervention.id,
        appointment_id: intervention.appointment_id,
        snooze_history: intervention.snooze_history,
        snooze_status_id: intervention.snooze?.snooze_status_id,
        user
      });
    else if (questionResult)
      setShowEditSnoozeQuestionResultModal({
        title: questionResult.title,
        description: questionResult.raw,
        solution: questionResult.solution,
        question_result_id: questionResult.id,
        appointment_id: questionResult.appointment_id,
        snooze_history: questionResult.snooze_history,
        user
      });
  };

  const handleConvertItemToIntervention = () => {
    if (intervention) {
      setShowConvertToInterventionModal({
        dms_nr: generateDMSNr(),
        title: intervention.title,
        description: intervention.description,
        price: intervention.price,
        customer_ok: false,
        elements: intervention.elements ?? [],
        appointment_id: appointment.id,
        snooze_id: snoozeItem.id,
        attachments: attachments.map(att => ({ ...att, created_on: "", updated_on: "", visible_to_customer: true }))
      });
    } else if (questionResult) {
      const convertedIntervention = convertQuestionResultToIntervention({
        title: questionResult.tyre ? tyreTitle : questionResult.title,
        questionResult,
        appointment,
        seasonOptions,
        location: appointmentLocation,
        showPriceInVat,
        snoozeItem
      });

      setShowConvertToInterventionModal(convertedIntervention);
    }
  };

  const handleQuestionResultAppendSnooze = async (snooze: Snooze) => {
    if (!canCreateSnooze) return;
    await appendQuestionResultSnooze.mutateAsync(snooze);
  };
  const handleQuestionResultDeleteSnooze = async (snooze: DeleteSnoozePayload) => {
    await deleteQuestionResultSnooze.mutateAsync(snooze);
  };
  const handleInterventionAppendSnooze = async (snooze: Snooze) => {
    await interventionAppendSnooze(snooze);
  };
  const handleInterventionDeleteSnooze = async (snooze: DeleteSnoozePayload) => {
    await interventionDeleteSnooze(snooze);
  };

  return (
    <>
      <div className="SnoozeActionSelector">
        <Dropdown
          disabled={!canConvertToIntervention && !canEditSnooze}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className="plus grey" />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertItemToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}
            {canEditSnooze && (
              <DropdownItem onClick={handleEditSnoozeItem}>
                <Icon className="pen" />
                {t("v8_edit", "Edit")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        location={appointmentLocation}
        dealer={appointmentDealer}
        open={showConvertToInterventionModal !== null}
        intervention={showConvertToInterventionModal}
        onClose={() => setShowConvertToInterventionModal(null)}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
        onInterventionCreated={intervention => handleSnoozeStatusToDone(intervention.question_result_id)}
      />
      {showEditSnoozedInterventionModal && (
        <SnoozeModal
          dealer={appointmentDealer}
          location={appointmentLocation}
          data={showEditSnoozedInterventionModal}
          open={Boolean(showEditSnoozedInterventionModal)}
          onSave={handleUpdateSnoozeItems}
          onClose={handleCloseSnoozeModals}
          onAppendSnooze={handleInterventionAppendSnooze}
          onDeleteSnooze={handleInterventionDeleteSnooze}
          isDeleteSnoozeLoading={isPendingInterventionDeleteSnooze}
          isAppendSnoozeLoading={isPendingInterventionAppendSnooze}
        />
      )}
      {showEditSnoozeQuestionResultModal && (
        <SnoozeModal
          dealer={appointmentDealer}
          location={appointmentLocation}
          data={showEditSnoozeQuestionResultModal}
          open={Boolean(showEditSnoozeQuestionResultModal)}
          onSave={handleUpdateSnoozeItems}
          onClose={handleCloseSnoozeModals}
          onAppendSnooze={handleQuestionResultAppendSnooze}
          onDeleteSnooze={handleQuestionResultDeleteSnooze}
          isDeleteSnoozeLoading={deleteQuestionResultSnooze.isPending}
          isAppendSnoozeLoading={appendQuestionResultSnooze.isPending}
        />
      )}
    </>
  );
};
